import { useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import { get, put } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { dateTime } from '../lib/format'
import { useResource } from '../lib/useResource'

type ConfigRow = {
  key: string
  value: Record<string, unknown>
  description: string
  version: number
  updated_by: string | null
  updated_at: string | null
}

/** Keys the product expects to exist. Showing them even when unset means an
 *  operator can see what is configurable rather than having to guess a key
 *  name that the app happens to read. */
const KNOWN_KEYS: { key: string; description: string; template: Record<string, unknown> }[] = [
  {
    key: 'onboarding.copy',
    description: 'The three welcome panes shown before sign-in.',
    template: { panes: [{ title: '', body: '' }] },
  },
  {
    key: 'copilot.system_prompt',
    description:
      'The co-pilot system prompt. Versioned: the previous text is in the audit log, which is what makes rollback possible.',
    template: { text: '', version_note: '' },
  },
  {
    key: 'feature.flags',
    description: 'Runtime feature switches read by the app on launch.',
    template: { nfc_access: false, break_glass: false, research_export: false },
  },
  {
    key: 'thresholds.defaults',
    description: 'Trigger defaults applied to a new account before the person customises them.',
    template: { systolic_max: 180, diastolic_max: 120, hr_min: 30, hr_max: 220, spo2_min: 88 },
  },
  {
    key: 'notifications.templates',
    description: 'Message bodies for the WhatsApp and SMS dispatch legs.',
    template: { whatsapp: '', sms: '' },
  },
  {
    key: 'maintenance.banner',
    description: 'Shown at the top of every app screen while set. Empty text hides it.',
    template: { text: '', severity: 'info', until: '' },
  },
]

export function Config() {
  const { can } = useAuth()
  const config = useResource((signal) => get<ConfigRow[]>('/v1/admin/config', undefined, signal))
  const [editing, setEditing] = useState<{ key: string; value: unknown; description: string } | null>(
    null,
  )

  const stored = new Map((config.data ?? []).map((row) => [row.key, row]))
  const rows = [
    ...KNOWN_KEYS.map((known) => ({
      ...known,
      stored: stored.get(known.key),
    })),
    ...(config.data ?? [])
      .filter((row) => !KNOWN_KEYS.some((k) => k.key === row.key))
      .map((row) => ({
        key: row.key,
        description: row.description || 'Custom key.',
        template: row.value,
        stored: row,
      })),
  ]

  return (
    <Page
      title="Content & config"
      subtitle="Non-secret runtime configuration. Credentials are not here and never will be — a value an admin can read out of a console is not a secret."
    >
      {!can(P.configWrite) && (
        <Notice>
          You can read configuration but not change it — that needs{' '}
          <span className="mono">config:write</span>.
        </Notice>
      )}

      <Notice>
        Every write increments a version and records the previous value in the audit log against
        the same key. To roll back, open the audit entry, copy the <span className="mono">before</span>{' '}
        block, and save it as the new value — the rollback is itself an audited write rather than a
        silent reversion.
      </Notice>

      <Card flush>
        <Loaded resource={config}>
          {() => (
            <Table
              rows={rows}
              rowKey={(row) => row.key}
              onRowClick={(row) =>
                can(P.configWrite)
                  ? setEditing({
                      key: row.key,
                      value: row.stored?.value ?? row.template,
                      description: row.description,
                    })
                  : undefined
              }
              empty="No configuration keys."
              columns={[
                { key: 'k', header: 'Key', strong: true, render: (row) => <span className="mono">{row.key}</span> },
                {
                  key: 'desc',
                  header: 'What it controls',
                  render: (row) => <span className="dim">{row.description}</span>,
                },
                {
                  key: 'state',
                  header: 'State',
                  render: (row) =>
                    row.stored ? (
                      <Badge tone="green">v{row.stored.version}</Badge>
                    ) : (
                      <Badge tone="orange">Not set — app default in force</Badge>
                    ),
                },
                {
                  key: 'updated',
                  header: 'Last change',
                  render: (row) => (
                    <span className="dim">
                      {row.stored ? dateTime(row.stored.updated_at) : '—'}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      {editing && (
        <ConfigEditor
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            config.reload()
          }}
        />
      )}
    </Page>
  )
}

function ConfigEditor({
  entry,
  onClose,
  onSaved,
}: {
  entry: { key: string; value: unknown; description: string }
  onClose: () => void
  onSaved: () => void
}) {
  const run = useAction()
  const [text, setText] = useState(() => JSON.stringify(entry.value, null, 2))
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  let parsed: Record<string, unknown> | null = null
  let parseError: string | null = null
  try {
    const candidate: unknown = JSON.parse(text)
    if (candidate === null || typeof candidate !== 'object' || Array.isArray(candidate)) {
      parseError = 'The value must be a JSON object, not an array or a bare literal.'
    } else {
      parsed = candidate as Record<string, unknown>
    }
  } catch (caught) {
    parseError = caught instanceof Error ? caught.message : 'Invalid JSON'
  }

  const save = async () => {
    if (!parsed) return
    setBusy(true)
    await run(
      () => put(`/v1/admin/config/${encodeURIComponent(entry.key)}`, { value: parsed, reason: reason.trim() }),
      `${entry.key} saved.`,
      onSaved,
    )
    setBusy(false)
  }

  return (
    <Modal title={<span className="mono">{entry.key}</span>} onClose={onClose}>
      <div className="stack">
        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
          {entry.description}
        </p>
        <div className="field">
          <label className="field__label" htmlFor="config-value">
            Value (JSON object)
          </label>
          <textarea
            id="config-value"
            className="textarea mono"
            style={{ minHeight: 200, fontSize: 12 }}
            value={text}
            onChange={(event) => setText(event.target.value)}
            spellCheck={false}
          />
        </div>
        {parseError && <Notice tone="warn">{parseError}</Notice>}
        <div className="field">
          <label className="field__label" htmlFor="config-reason">
            Reason (recorded in the audit log)
          </label>
          <input
            id="config-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="What changed and why"
          />
        </div>
        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={busy || parsed === null || reason.trim().length < 3}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
